import { useCallback, useState, useEffect } from "react";
import {
  validate,
  EditComponentErrorResponse,
  FormSchemaDto,
  EditComponentErrorGroup,
} from "@amzn/crowd-front-end-common-library";
import { AxiosError } from "axios";
import { EditTeamResult } from "./EditTeamResult";
import useAsync from "../../http/useAsync";
import { MetricsState } from "../../logging";
import httpRequestWithMetrics from "../../http/httpRequestWithMetrics";
import { LoadingState } from "../../http";
import { EditTeamUpdateDto } from "../../common/model/edit-team-update.dto";
import { DisplayBanner } from "../../banner/bannerSlice";
import {
  changesAfterFirstFailureBanner,
  changesFirstFailureBanner,
} from "../../common/utils/ErrorBannerMessages";

interface UseEditTeamValidateHookResult {
  execute: () => void;
  thrownValidationError?: Error;
  loadingState: LoadingState;
  validationErrors?: EditComponentErrorResponse;
  asyncValidateResult?: EditComponentErrorResponse;
}

async function validateInputAsync(
  originalTeam: EditTeamResult,
  metrics: MetricsState,
  attributesToUpdate: EditTeamUpdateDto[]
): Promise<any> {
  const response = await httpRequestWithMetrics(
    "editTeamValidateAsync",
    metrics,
    {
      url: `/api/v1/teams/${originalTeam.teamId}/`,
      method: "PUT",
      data: {
        updates: attributesToUpdate,
      },
    }
  );
  return response.data;
}

/**
 * Custom react hook that abstracts the validation logic for edit team form.
 * This hook also commits the update if the validation passes.
 *
 * @param {FormSchemaDto} formSchema
 * @param {Partial<EditTeamResult>} localChanges
 * @param {MetricsState} metrics
 * @param {(diff: boolean) => void} onEditTeamPass
 * @param {(banner: DisplayBanner | undefined) => void} setBanner
 * @param {EditTeamResult} originalTeam
 * @param {EditTeamUpdateDto[]} attributesToUpdate
 * @param {boolean} showEditForm
 * @returns {UseCreateTeamValidateHookResult}
 */
function useEditTeamValidate(
  formSchema: FormSchemaDto,
  localChanges: Partial<EditTeamResult>,
  metrics: MetricsState,
  onEditTeamPass: (attributes: EditTeamUpdateDto[]) => void,
  setBanner: (banner: DisplayBanner | undefined) => void,
  originalTeam: EditTeamResult,
  attributesToUpdate: EditTeamUpdateDto[],
  showEditForm: boolean
): UseEditTeamValidateHookResult {
  const [validationErrors, setValidationErrors] = useState<
    EditComponentErrorResponse | undefined
  >();
  const [submitTimes, setSubmitTimes] = useState(0);
  useEffect(() => {
    if (!showEditForm) {
      setValidationErrors(undefined);
    }
  }, [showEditForm]);

  const {
    loadingState,
    execute: executeAsyncValidate,
    error,
    response: asyncValidateResult,
  } = useAsync(validateInputAsync, [originalTeam, metrics, attributesToUpdate]);

  useEffect(() => {
    const httpError = error as AxiosError;
    if (httpError && httpError.response && httpError.response.status === 400) {
      // 400 Errors are validation errors. Treat other errors differently.
      setValidationErrors(httpError.response.data);
    }
  }, [error]);

  useEffect(() => {
    if (asyncValidateResult && asyncValidateResult.errors.length > 0) {
      const errors = asyncValidateResult.errors.map(
        (asyncError: { attribute: string; errorMessage: string }) => {
          return {
            fieldName: asyncError.attribute,
            message: asyncError.errorMessage,
            validation: "asyncValidation",
          };
        }
      );

      const asyncError: EditComponentErrorResponse = {
        errors: [
          {
            groupId: "edit-team-group",
            groupLabel: "Team edit information",
            errors,
          },
        ],
      };
      setValidationErrors(asyncError);
    } else if (
      loadingState === LoadingState.RESOLVED &&
      asyncValidateResult.errors.length === 0
    ) {
      setValidationErrors(undefined);
      onEditTeamPass(attributesToUpdate);
    } else if (loadingState === LoadingState.REJECTED) {
      // TODO: handle 404 differently
      setValidationErrors(undefined);
      if (submitTimes < 2) {
        setBanner(changesFirstFailureBanner);
      } else {
        setBanner(changesAfterFirstFailureBanner);
      }
    }
  }, [
    loadingState,
    asyncValidateResult,
    onEditTeamPass,
    setBanner,
    submitTimes,
    attributesToUpdate,
  ]);

  useEffect(() => {
    if (loadingState === LoadingState.REJECTED) {
      setSubmitTimes((times) => times + 1);
    }
  }, [setSubmitTimes, loadingState]);

  const execute = useCallback(() => {
    const modifiedTeam = { ...localChanges };
    /*
     * When user doesn't update team name/owner,
     * the existing team name/owner is considered for sync validation to avoid the related validation errors.
     * This is not required for other attributes as they are not mandatory elements
     * */
    if (!Object.prototype.hasOwnProperty.call(modifiedTeam, "name")) {
      // The existing team name is only used when user doesn't updated the team name.
      modifiedTeam.name = originalTeam.name;
    }
    if (!Object.prototype.hasOwnProperty.call(modifiedTeam, "owner")) {
      // The existing team owner is only used when user doesn't updated the team owner.
      modifiedTeam.owner = originalTeam.owner;
    }
    const syncValidateResult = validate(modifiedTeam, formSchema);
    if (modifiedTeam.ownerInputValue && !modifiedTeam.owner) {
      const errors = [
        {
          fieldName: "owner",
          message:
            "Team owner is not valid. Select an employee from the search results.",
          validation: "ownerInputValueValidation",
        },
      ];
      const ownerInputValueError: EditComponentErrorGroup = {
        groupId: "general-group",
        groupLabel: "Team",
        errors,
      };
      syncValidateResult.errors.push(ownerInputValueError);
    }
    if (syncValidateResult.errors.length > 0) {
      setValidationErrors(syncValidateResult);
    } else {
      setValidationErrors(undefined);
      executeAsyncValidate();
    }
  }, [
    localChanges,
    formSchema,
    originalTeam.name,
    originalTeam.owner,
    executeAsyncValidate,
  ]);

  return {
    execute,
    thrownValidationError: error as Error | undefined,
    loadingState,
    validationErrors,
  };
}

export default useEditTeamValidate;
